import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import OpenAI from "openai";
import cardBg from "../images/mbti_card.png";
import bgMain from "../images/bg-main.png";
import PromptEditor from "./PromptEditor";
import ShareButtons from "./common/ShareButtons";
import {
  getWalletData,
  fetchWalletSummary,
  formatTradeSummary,
} from "../services/walletService";

// 可以在这里导入MBTI类型图片
import istjImage from "../images/mbti/types/istj.png";
import isfjImage from "../images/mbti/types/isfj.png";
import infjImage from "../images/mbti/types/infj.png";
import intjImage from "../images/mbti/types/intj.png";
import istpImage from "../images/mbti/types/istp.png";
import isfpImage from "../images/mbti/types/isfp.png";
import infpImage from "../images/mbti/types/infp.png";
import intpImage from "../images/mbti/types/intp.png";
import estpImage from "../images/mbti/types/estp.png";
import esfpImage from "../images/mbti/types/esfp.png";
import enfpImage from "../images/mbti/types/enfp.png";
import entpImage from "../images/mbti/types/entp.png";
import estjImage from "../images/mbti/types/estj.png";
import esfjImage from "../images/mbti/types/esfj.png";
import enfjImage from "../images/mbti/types/enfj.png";
import entjImage from "../images/mbti/types/entj.png";

const MBTIDetail = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState("form");
  const [isLoading, setIsLoading] = useState(false);
  const [result, setResult] = useState("");
  const [parsedResult, setParsedResult] = useState(null);
  const [progress, setProgress] = useState(0);
  const [progressStatus, setProgressStatus] = useState("Analyzing...");
  const [streamComplete, setStreamComplete] = useState(false);
  const [controller, setController] = useState(null);
  const resultRef = useRef(null);
  const [currentPrompt, setCurrentPrompt] = useState(`Based on the user's BSC wallet transaction history, analyze their Web3 trading style and determine their MBTI personality type.

**Wallet Transaction Data:**
{{tradeSummary}}

Consider the following aspects:

1. Energy Source: Extraversion (E) vs. Introversion (I)
   - Extraversion (E): Frequent trading, multiple token holdings, active in social tokens
   - Introversion (I): Long-term holding, focus on fewer tokens, less frequent trading

2. Information Collection: Sensing (S) vs. Intuition (N)
   - Sensing (S): Focus on practical value and stable tokens, participate in mature projects
   - Intuition (N): Invest in emerging tokens, participate in early-stage projects, focus on innovative technology

3. Decision Making: Thinking (T) vs. Feeling (F)
   - Thinking (T): Trade based on data and technical analysis, avoid emotional projects
   - Feeling (F): Follow community trends, participate in social tokens and meme coins

4. Lifestyle: Judging (J) vs. Perceiving (P)
   - Judging (J): Regular trading patterns, DCA strategy, long-term holding
   - Perceiving (P): Flexible trading, seize short-term opportunities, adapt to market changes

Please return the following information in JSON format:
{
  "tag": "XXXX", // Must be a complete four-letter MBTI type, e.g., INTJ, ESFP, etc., do not use underscores or other separators
  "title": "Personality Type Title", // e.g., Architect/Strategist, Campaigner/Advocate, etc.
  "behavior": "Personality Description", // Brief summary of trading characteristics
  "strengths": ["Strength 1", "Strength 2", "Strength 3"], // Array format, list 3 investment strengths
  "weaknesses": ["Weakness 1", "Weakness 2", "Weakness 3"], // Array format, list 3 investment weaknesses
  "investmentStyle": "Investment Style Description" // Brief description of investment style
}

Ensure the return is in valid JSON format with no other text. Must include all fields, and the tag must be one of the 16 MBTI types (ISTJ, ISFJ, INFJ, INTJ, ISTP, ISFP, INFP, INTP, ESTP, ESFP, ENFP, ENTP, ESTJ, ESFJ, ENFJ, ENTJ).`);
  // 这些状态变量用于钱包摘要功能
  const [summaryData, setSummaryData] = useState(null);
  const [isSummaryLoading, setIsSummaryLoading] = useState(false);
  const [tradeSummary, setTradeSummary] = useState("");
  const [promptReady, setPromptReady] = useState(false);
  const [walletAddress, setWalletAddress] = useState("");

  // 处理返回按钮
  const handleBackToForm = () => {
    if (parsedResult && step === "result") {
      // If on result page, first return to form state
      setParsedResult(null);
      setStep("form");
      setResult("");
      setProgress(0);
      setStreamComplete(false);
      if (controller) {
        controller.abort();
      }
    } else {
      // Otherwise return to home page
      navigate(-1);
    }
  };

  // 获取钱包数据
  const handleGetWalletData = useCallback(
    async (address) => {
      if (!address) return;

      try {
        setIsSummaryLoading(true);

        // 获取钱包数据
        const walletData = await getWalletData(address);

        if (!walletData) {
          console.log("No transaction records found, trying to refresh data");
          // 如果没有数据或交易为空，尝试刷新数据
          await fetchWalletSummary(address);
          const refreshedData = await getWalletData(address);
          setSummaryData(refreshedData);

          // 格式化交易摘要
          if (refreshedData) {
            const summary = formatTradeSummary(refreshedData);
            setTradeSummary(summary);
            // 替换提示词中的占位符
            const updatedPrompt = currentPrompt.replace(
              "{{tradeSummary}}",
              summary
            );
            setCurrentPrompt(updatedPrompt);
            setPromptReady(true);
          } else {
            console.error("No trade data available after refresh");
          }
        } else {
          // 如果有数据，直接使用
          setSummaryData(walletData);

          // 格式化交易摘要
          const summary = formatTradeSummary(walletData);
          setTradeSummary(summary);
          // 替换提示词中的占位符
          const updatedPrompt = currentPrompt.replace("{{tradeSummary}}", summary);
          setCurrentPrompt(updatedPrompt);
          setPromptReady(true);
        }
      } catch (error) {
        console.error("Error getting wallet data:", error);
      } finally {
        setIsSummaryLoading(false);
      }
    },
    [currentPrompt]
  );

  // 获取钱包地址和数据
  useEffect(() => {
    const savedAddress = localStorage.getItem("bscAddress");
    if (savedAddress) {
      setWalletAddress(savedAddress);
      handleGetWalletData(savedAddress);
    }
  }, [handleGetWalletData]);

  // 处理表单提交和API请求
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setStep("result");

    // 创建一个可中止的控制器
    const abortController = new AbortController();
    setController(abortController);

    // 创建OpenAI客户端
    const openai = new OpenAI({
      baseURL: "https://api.deepseek.com",
      apiKey: "sk-326e542c536742f3bb5f4d8b019a614e",
      dangerouslyAllowBrowser: true,
    });

    // 初始化进度
    setProgress(0);
    setProgressStatus("Analyzing...");

    // 模拟进度更新
    const progressInterval = setInterval(() => {
      setProgress((prev) => {
        if (prev >= 95) {
          clearInterval(progressInterval);
          return prev;
        }
        const increment = Math.random() * 10;
        const newProgress = Math.min(prev + increment, 95);

        // 根据进度更新状态消息
        if (newProgress < 30) {
          setProgressStatus("Analyzing wallet transactions...");
        } else if (newProgress < 60) {
          setProgressStatus("Identifying trading patterns...");
        } else if (newProgress < 90) {
          setProgressStatus("Generating Web3 MBTI type...");
        }

        return newProgress;
      });
    }, 1000);

    try {
      // Get BSC address from localStorage if not in form
      const bscAddress = walletAddress || localStorage.getItem("bscAddress");

      if (!bscAddress) {
        throw new Error("BSC address is required");
      }

      // Build prompt - 使用已经准备好的prompt
      const prompt = currentPrompt;

      // 发送请求到OpenAI（非流式）
      const response = await openai.chat.completions.create(
        {
          model: "deepseek-chat",
          messages: [{ role: "system", content: prompt }],
          stream: false,
        },
        { signal: abortController.signal }
      );

      // 获取完整的响应文本
      const responseText = response.choices[0]?.message?.content || "";
      setResult(responseText);

      // 尝试解析JSON
      try {
        // 处理可能的Markdown代码块
        let cleanedResponse = responseText;
        if (responseText.includes('```json')) {
          cleanedResponse = responseText.replace(/```json\n|```/g, '');
        }
        
        // 查找JSON对象的开始和结束
        const jsonStart = cleanedResponse.indexOf("{");
        const jsonEnd = cleanedResponse.lastIndexOf("}");

        if (jsonStart !== -1 && jsonEnd !== -1 && jsonEnd > jsonStart) {
          const jsonStr = cleanedResponse.substring(jsonStart, jsonEnd + 1);
          const parsed = JSON.parse(jsonStr);
          if (parsed && parsed.tag) {
            // 处理tag格式，去掉可能的下划线
            if (parsed.tag && parsed.tag.includes('_')) {
              parsed.tag = parsed.tag.replace(/_/g, '');
            }
            
            // 如果没有title字段，根据tag生成一个标题
            if (!parsed.title) {
              // 根据tag生成对应的MBTI类型标题
              const tagTitleMap = {
                ISTJ: "Inspector/Logistician",
                ISFJ: "Protector/Defender",
                INFJ: "Advocate/Counselor",
                INTJ: "Architect/Strategist",
                ISTP: "Craftsman/Virtuoso",
                ISFP: "Artist/Adventurer",
                INFP: "Mediator/Healer",
                INTP: "Thinker/Logician",
                ESTP: "Entrepreneur/Performer",
                ESFP: "Entertainer/Performer",
                ENFP: "Campaigner/Champion",
                ENTP: "Debater/Inventor",
                ESTJ: "Director/Executive",
                ESFJ: "Consul/Provider",
                ENFJ: "Teacher/Protagonist",
                ENTJ: "Commander/Field Marshal",
              };
              parsed.title = tagTitleMap[parsed.tag] || `${parsed.tag} Type`;
            }
            
            // 添加behavior字段，如果不存在
            if (!parsed.behavior) {
              parsed.behavior = "Long-term holding, focus on fewer tokens";
            }
            
            // 添加strengths和weaknesses字段，如果不存在
            if (!parsed.strengths) {
              parsed.strengths = ["Stable investment", "Long-term planning", "Avoiding emotional decisions"];
            } else if (typeof parsed.strengths === 'string') {
              try {
                parsed.strengths = JSON.parse(parsed.strengths);
              } catch (e) {
                parsed.strengths = [parsed.strengths];
              }
            }
            
            if (!parsed.weaknesses) {
              parsed.weaknesses = ["Lack of flexibility", "May miss emerging opportunities", "Low trading frequency"];
            } else if (typeof parsed.weaknesses === 'string') {
              try {
                parsed.weaknesses = JSON.parse(parsed.weaknesses);
              } catch (e) {
                parsed.weaknesses = [parsed.weaknesses];
              }
            }
            
            if (!parsed.investmentStyle) {
              parsed.investmentStyle = "Conservative and stable, focusing on long-term holding and practical value";
            }
            
            setParsedResult(parsed);
          }
        }
      } catch (jsonError) {
        console.error("JSON parsing failed:", jsonError);
      }

      // 完成后更新进度到100%
      setProgress(100);
      setProgressStatus("Analysis complete！");
      setStreamComplete(true);
    } catch (error) {
      console.error("Error:", error);
      setResult(`Error: ${error.message}`);
      setStreamComplete(true);
    } finally {
      // 清除进度条定时器
      clearInterval(progressInterval);
      setIsLoading(false);
    }
  };

  // 确保每次结果更新时滚动到底部
  useEffect(() => {
    if (resultRef.current && result) {
      if (resultRef.current) {
        resultRef.current.scrollTop = resultRef.current.scrollHeight;
      }
    }
  }, [result, streamComplete]);

  // 组件卸载时清理资源
  useEffect(() => {
    return () => {
      if (controller) {
        controller.abort();
      }
    };
  }, [controller]);

  // Handle prompt update from PromptEditor
  const handleSendPrompt = (newPrompt) => {
    setCurrentPrompt(newPrompt);
  };

  // Get the appropriate image based on tag
  const getTagImage = (tag) => {
    if (!tag) return cardBg;

    const tagMap = {
      ISTJ: istjImage,
      ISFJ: isfjImage,
      INFJ: infjImage,
      INTJ: intjImage,
      ISTP: istpImage,
      ISFP: isfpImage,
      INFP: infpImage,
      INTP: intpImage,
      ESTP: estpImage,
      ESFP: esfpImage,
      ENFP: enfpImage,
      ENTP: entpImage,
      ESTJ: estjImage,
      ESFJ: esfjImage,
      ENFJ: enfjImage,
      ENTJ: entjImage,
    };

    return tagMap[tag] || cardBg;
  };

  return (
    <div className="relative min-h-screen w-full overflow-hidden">
      {/* Background elements */}
      <div className="fixed inset-0 overflow-hidden">
        <div
          className="absolute w-full h-full overflow-hidden"
          style={{
            backgroundImage: `url(${bgMain})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            opacity: 0.8,
          }}
        ></div>
      </div>

      {/* Main content */}
      <div className="relative z-10 w-full max-w-md mx-auto pt-8 px-4 pb-20">
        {/* Header */}
        <div className="flex items-center justify-between mb-6 relative">
          <button
            onClick={handleBackToForm}
            className="text-[#1c1f25] hover:text-[#f784ff] transition-colors z-10"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-6 w-6"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 19l-7-7 7-7"
              />
            </svg>
          </button>
          <h2 className="text-[#1c1f25] text-xl font-medium absolute left-0 right-0 text-center">
            Web3 Trading MBTI
          </h2>
        </div>

        <AnimatePresence mode="wait">
          {/* Main card with progress bar - Show when loading but no result yet */}
          {(step === "form" || (isLoading && !parsedResult)) && (
            <motion.div
              key="main-card"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              transition={{ duration: 0.4 }}
              className="mt-6 w-[345px] mx-auto"
            >
              {/* Memecoin Tag Card - Based on Figma design */}
              <div className="flex flex-col w-[345px] items-start gap-[15px] p-[15px] relative bg-[rgba(255,255,255,0.75)] rounded-[16px] overflow-hidden shadow-lg mx-auto">
                <div className="flex flex-col items-center gap-2.5 relative self-stretch w-full flex-[0_0_auto]">
                  {/* Card Header with background image */}
                  <img
                    className="relative self-stretch w-full h-[255px] object-cover"
                    src={cardBg}
                    alt="Card Background"
                  />

                  {/* Title */}
                  <p className="relative w-fit font-medium text-[#1c1f25] text-xl tracking-[0] leading-[35px] whitespace-nowrap">
                    Web3 Trading MBTI
                  </p>

                  {/* Subtitle */}
                  <p className="relative self-stretch font-normal text-[#676C77] text-base tracking-[0] leading-[23px]">
                    Meet your AI-generated MBTI persona — uniquely crafted from
                    your Web3 wallet activity. Dive deeper into your on-chain
                    identity and uncover who you truly are in the world of Web3.
                  </p>

                  {/* Progress bar inside card */}
                  {isLoading && (
                    <div className="w-full mt-4 flex flex-col items-center">
                      <div className="flex flex-col w-full items-start gap-2.5">
                        {/* Progress bar */}
                        <div className="flex flex-col w-full items-start gap-2.5 relative flex-[0_0_auto] bg-[#cacfdb] rounded-[100px] border-[0.5px] border-solid border-[#E5E7EB]">
                          <div
                            className="relative h-[15px] rounded-[100px]"
                            style={{
                              width: `${progress}%`,
                              background:
                                "linear-gradient(72deg, rgba(112,235,122,1) 1%, rgba(95,217,200,1) 1%, rgba(112,168,235,1) 36%, rgba(116,166,236,1) 45%, rgba(179,142,255,1) 64%, rgba(227,86,197,1) 85%, rgba(249,173,88,1) 100%)",
                            }}
                          />
                        </div>

                        {/* Status text */}
                        <div className="relative self-stretch font-normal text-[#959AA3] text-sm text-center tracking-[0] leading-[23px]">
                          {Math.floor(progress)}%&nbsp;&nbsp;&nbsp;&nbsp;
                          {progressStatus}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Button below card - Only show in form step */}
              {step === "form" && (
                <motion.form
                  onSubmit={handleSubmit}
                  key="form-button"
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                  transition={{ duration: 0.4 }}
                  className="mt-6 w-[345px] mx-auto"
                >
                  <motion.button
                    type="submit"
                    className="w-full bg-gradient-to-r from-[#f784ff] to-[#b784ff] text-white py-3 rounded-lg font-medium hover:opacity-90 transition-all shadow-md flex items-center justify-center"
                    whileHover={{
                      scale: 1.05,
                      boxShadow: "0 10px 25px -5px rgba(247, 132, 255, 0.4)",
                    }}
                    whileTap={{ scale: 0.95 }}
                    disabled={isLoading || !promptReady || isSummaryLoading}
                  >
                    {isLoading ? (
                      <div className="flex items-center">
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                        Analyzing...
                      </div>
                    ) : isSummaryLoading ? (
                      <div className="flex items-center">
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                        Loading wallet data...
                      </div>
                    ) : (
                      "Start Analysis"
                    )}
                  </motion.button>
                </motion.form>
              )}
            </motion.div>
          )}

          {/* Result card - Only show when there's a parsed result */}
          {parsedResult && (
            <motion.div
              key="result-content"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              transition={{ duration: 0.4 }}
              className="mt-6 w-[355px] mx-auto"
            >
              {/* New Result Card Design */}
              <div
                id="meme-tag-card"
                className="relative w-[355px] min-h-[750px] bg-[#0a0e1f] rounded-[16px] overflow-hidden border border-solid border-transparent pb-8"
              >
                {/* Tag Image */}
                <motion.div className="w-full h-[547px] flex-shrink-0 self-stretch overflow-hidden">
                  <motion.img
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                    className="w-full h-full object-cover"
                    src={getTagImage(parsedResult.tag)}
                    alt={parsedResult.tag}
                  />
                </motion.div>

                <motion.div 
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.5, delay: 0.2 }}
                  className="px-7 flex flex-col space-y-5"
                >
                  {/* Title */}
                  <motion.div 
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.3, delay: 0.3 }}
                    className="w-full mt-5 font-bold text-[22px] text-center tracking-[0] leading-[normal] text-white"
                  >
                    {parsedResult.title || `${parsedResult.tag} Type`}
                  </motion.div>

                  {/* MBTI Type */}
                  <motion.div 
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.3, delay: 0.4 }}
                    className="w-full font-bold text-[18px] text-center tracking-[0] leading-[normal] text-[#f784ff]"
                  >
                    {parsedResult.tag}
                  </motion.div>

                  {/* Behavior Description */}
                  <motion.div 
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.4, delay: 0.5 }}
                    className="w-full font-normal text-[#f5f5f5] text-base tracking-[0] leading-[normal]"
                  >
                    <span className="font-bold">
                      Personality Description
                      <br />
                    </span>

                    <span className="font-medium">{parsedResult.behavior}</span>
                  </motion.div>

                  {/* Investment Style */}
                  <motion.div 
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.4, delay: 0.6 }}
                    className="w-full font-normal text-[#f5f5f5] text-base tracking-[0] leading-[normal]"
                  >
                    <span className="font-bold">
                      Investment Style
                      <br />
                    </span>

                    <span className="font-medium">{parsedResult.investmentStyle}</span>
                  </motion.div>

                  {/* Strengths */}
                  <motion.div 
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.4, delay: 0.7 }}
                    className="w-full font-normal text-[#f5f5f5] text-base tracking-[0] leading-[normal]"
                  >
                    <span className="font-bold">
                      Investment Strengths
                      <br />
                    </span>
                    <ul className="list-disc pl-5 mt-1 space-y-1">
                      {parsedResult.strengths && parsedResult.strengths.map((strength, index) => (
                        <li key={index} className="font-medium">{strength}</li>
                      ))}
                    </ul>
                  </motion.div>

                  {/* Weaknesses */}
                  <motion.div 
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.4, delay: 0.8 }}
                    className="w-full font-normal text-[#f5f5f5] text-base tracking-[0] leading-[normal]"
                  >
                    <span className="font-bold">
                      Investment Weaknesses
                      <br />
                    </span>
                    <ul className="list-disc pl-5 mt-1 space-y-1">
                      {parsedResult.weaknesses && parsedResult.weaknesses.map((weakness, index) => (
                        <li key={index} className="font-medium">{weakness}</li>
                      ))}
                    </ul>
                  </motion.div>
                </motion.div>
              </div>

              {/* Bottom Button Group */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: 0.6 }}
                className="mt-6"
              >
                <ShareButtons
                  cardId="meme-tag-card"
                  data={parsedResult}
                  featureName="Web3 Trading MBTI"
                  shareText={`See my Web3 MBTI and then try yours: https://dapp.pinai.tech\n${parsedResult.tag} - ${parsedResult.title}\nMy investment style: ${parsedResult.investmentStyle}`}
                />
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Prompt Editor - Only show when data is loaded, not analyzing, and in form step */}
        {promptReady && !isLoading && step === "form" && (
          <PromptEditor
            updatePrompt={currentPrompt}
            onSendPrompt={handleSendPrompt}
          />
        )}
      </div>
    </div>
  );
};

export default MBTIDetail;
